/**
 * PujcovnaVyjimkyTool — the queue of payments that need a person (`?nastroj=vyjimky` on the Správa page, P6-e2; admin like the API): money the
 * gateway took that the books did not credit (an excess, a payment for a cancelled or already paid reservation) and late payments. Staff
 * either refund it or accept it as handled. A refund is the server's call: it works out what is left to give back and cannot repeat itself.
 * Re-booking money to another reservation and unmatched bank lines are not built (P6-h).
 */

import { useMemo, useState } from 'react';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { EmptyState } from '@/components/EmptyState';
import { SegmentedControl } from '@/components/SegmentedControl';
import { Skeleton } from '@/components/Skeleton';
import { Toolband } from '@/components/Toolband';
import { formatInt } from '@/lib/format';
import { useVyjimkyQuery, type PlatbaVyjimka, type VyjimkaStav } from './api';
import { datumCas } from './brana-helpers';
import { DRUH_VYJIMKY, providerNazev } from './platby-helpers';
import { VyjimkaVyrizeniModal } from './VyjimkaVyrizeniModal';

export function PujcovnaVyjimkyTool() {
  const [stav, setStav] = useState<VyjimkaStav>('otevrena');
  const query = useVyjimkyQuery(stav);
  const [reseni, setReseni] = useState<{ vyjimka: PlatbaVyjimka; akce: 'prijato' | 'vraceno' } | null>(null);
  const rows = useMemo(() => query.data?.pages.flatMap((p) => p.vyjimky) ?? [], [query.data]);
  const otevrene = query.data?.pages[0]?.otevrene;

  const columns: DataTableColumn<PlatbaVyjimka>[] = [
    {
      id: 'druh',
      header: 'Druh',
      width: 'minmax(220px, 1.4fr)',
      priority: 'primary',
      cell: (v) => {
        const druh = DRUH_VYJIMKY[v.druh];
        return (
          <span className="flex min-w-0 flex-col gap-1">
            <span>
              <Badge tone={druh.tone} size="sm">
                {druh.label}
              </Badge>
            </span>
            <span className="text-[12px] leading-snug text-ink-3">{druh.popis}</span>
          </span>
        );
      },
    },
    {
      id: 'rezervace',
      header: 'Rezervace',
      width: 'minmax(150px, 1fr)',
      priority: 'secondary',
      cell: (v) => (
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="font-mono text-[12px] text-ink">№ {v.id_rezervace}</span>
          <span className="truncate text-[12.5px] text-ink-2">{v.zakaznik_nazev ?? '—'}</span>
        </span>
      ),
    },
    {
      id: 'castka',
      header: 'Částka',
      width: '110px',
      align: 'right',
      priority: 'primary',
      cell: (v) => <span className="font-mono text-[13px] text-ink">{formatInt(v.castka)} Kč</span>,
    },
    {
      id: 'platba',
      header: 'Platba',
      width: 'minmax(150px, 1fr)',
      priority: 'secondary',
      cell: (v) => (
        <span className="flex flex-col gap-0.5 text-[12.5px] text-ink-2">
          <span>{providerNazev(v.provider)}</span>
          <span className="font-mono text-[11px] text-ink-3">№ {v.id_platby}</span>
        </span>
      ),
    },
    {
      id: 'kdy',
      header: stav === 'otevrena' ? 'Vzniklo' : 'Vyřízeno',
      width: '150px',
      priority: 'meta',
      cell: (v) => <span className="font-mono text-[12px] text-ink-3">{datumCas(stav === 'otevrena' ? v.vytvoreno_at : (v.vyrizeno_at ?? v.vytvoreno_at))}</span>,
    },
    {
      id: 'poznamka',
      header: 'Poznámka',
      width: 'minmax(160px, 1.2fr)',
      priority: 'meta',
      cell: (v) => (
        <span data-testid={`vyjimka-poznamka-${v.id}`} title={v.poznamka} className="line-clamp-3 text-[12px] text-ink-2">
          {v.poznamka || '—'}
        </span>
      ),
    },
    {
      id: 'akce',
      header: '',
      width: '250px',
      align: 'right',
      priority: 'meta',
      cell: (v) =>
        v.stav === 'otevrena' ? (
          <span className="flex flex-wrap justify-end gap-2">
            {v.lze_vratit && (
              <Button variant="secondary" size="sm" data-testid={`vyjimka-vratit-${v.id}`} onClick={() => setReseni({ vyjimka: v, akce: 'vraceno' })}>
                Vrátit peníze…
              </Button>
            )}
            <Button variant="secondary" size="sm" data-testid={`vyjimka-prijmout-${v.id}`} onClick={() => setReseni({ vyjimka: v, akce: 'prijato' })}>
              Vyřízeno…
            </Button>
          </span>
        ) : null,
    },
  ];

  return (
    <>
      <Toolband>
        <SegmentedControl<VyjimkaStav>
          aria-label="Stav položek"
          value={stav}
          onChange={setStav}
          options={[
            { value: 'otevrena', label: 'K řešení' },
            { value: 'vyrizena', label: 'Vyřízené' },
          ]}
        />
        <Toolband.Spacer />
        <span data-testid="vyjimky-pocet" className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
          {otevrene == null ? '—' : otevrene === 0 ? 'Nic k řešení' : `${otevrene} k řešení`}
        </span>
      </Toolband>
      <div data-testid="vyjimky-tool" className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto px-8 pb-8 pt-5">
        <p className="max-w-[720px] text-[13px] leading-relaxed text-ink-2">
          Sem se dostane platba, kterou brána přijala, ale rezervaci se nepřipsala: přeplatek, druhá platba za zaplacenou rezervaci nebo platba po zrušení. Peníze vraťte zákazníkovi, nebo položku označte jako vyřízenou.
        </p>
        {query.isPending ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : query.isError && rows.length === 0 ? (
          <EmptyState eyebrow="Chyba" title="Frontu plateb se nepodařilo načíst" description="Zkuste to prosím znovu za chvíli." />
        ) : rows.length === 0 ? (
          <EmptyState
            eyebrow={stav === 'otevrena' ? 'Vše vyřízeno' : 'Bez záznamů'}
            title={stav === 'otevrena' ? 'Žádná platba nečeká na vyřízení' : 'Zatím nic nebylo vyřízeno'}
          />
        ) : (
          <div className="flex flex-col gap-3">
            <DataTable<PlatbaVyjimka> aria-label="Fronta plateb k řešení" variant="bordered" columns={columns} data={rows} getRowId={(v) => String(v.id)} />
            {query.hasNextPage && (
              <div>
                <Button variant="secondary" size="sm" disabled={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>
                  Načíst další
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
      {reseni && (
        // one instance per item and action: the dialog's note starts empty
        <VyjimkaVyrizeniModal key={`${reseni.vyjimka.id}-${reseni.akce}`} vyjimka={reseni.vyjimka} akce={reseni.akce} open onOpenChange={(open) => !open && setReseni(null)} />
      )}
    </>
  );
}
